import React, { useEffect, useCallback } from 'react';
import { Row, Col, Spin, PageHeader, Radio, Card, List, Statistic } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import BarChart from '../BarChart/BarChart';
import {
  useStatistics,
  useStatisticsStoreLoading,
  useStatisticsPeriod,
  useStatisticsFacade,
} from '../../store/hooks';
import { RouteComponentProps } from 'react-router-dom';
import { StatisticsPeriod } from '../../types';
import UsersRatioChart from '../UsersRatioChart/UsersRatioChart';
import {
  useWebsitesFacade,
  useActiveWebsite,
} from '../../../websites/store/hooks';

const block = 'statistics';

const Statistics: React.FC<RouteComponentProps<{ id: string }>> = ({
  history,
  match,
}) => {
  const websiteId = match.params.id;
  const activeWebsite = useActiveWebsite();
  const statistics = useStatistics();
  const isLoading = useStatisticsStoreLoading();
  const period = useStatisticsPeriod();
  const facade = useStatisticsFacade();
  const websitesFacade = useWebsitesFacade();

  useEffect(() => {
    websitesFacade.setActiveWebsite(websiteId);

    return () => {
      websitesFacade.setActiveWebsite(null);
    };
  }, [websiteId, websitesFacade]);

  useEffect(() => {
    if (period) {
      facade.loadByPeriod(websiteId, period);
    }
  }, [websiteId, period, facade]);

  const handleBack = useCallback(() => {
    history.replace('/admin/websites');
  }, [history]);

  const handlePeriodChange = useCallback(
    (e: RadioChangeEvent) => {
      facade.setPeriod(e.target.value as StatisticsPeriod);
    },
    [facade]
  );

  const content = (
    <div className={block}>
      <div className={`${block}__header`}>
        <PageHeader
          onBack={handleBack}
          title={`Statistics for website '${activeWebsite?.title}'`}
          extra={
            <Radio.Group
              value={period}
              buttonStyle="solid"
              onChange={handlePeriodChange}
            >
              <Radio.Button value="lastDay">Last 24-hours</Radio.Button>
              <Radio.Button value="lastWeek">Last week</Radio.Button>
              <Radio.Button value="lastMonth">Last month</Radio.Button>
            </Radio.Group>
          }
        />
      </div>
      <div className={`${block}__content`}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={24} lg={12}>
            <BarChart
              title={'Users on site'}
              data={statistics?.users}
              xField={'date'}
              yField={'users'}
              xTitle={'Date'}
              yTitle={'Users'}
              period={period}
            />
          </Col>
          <Col xs={24} md={24} lg={12}>
            <Card className={'ant-card--chart'} title={'Most successful links'}>
              <List
                dataSource={statistics?.topLinks}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.title}
                      description={
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.link}
                        </a>
                      }
                    />
                    <Statistic value={item.visits} />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col xs={24} md={24} lg={12}>
            <BarChart
              title={'Average time on site'}
              data={statistics?.averageTime}
              xField={'date'}
              yField={'time'}
              xTitle={'Date'}
              yTitle={'Time in minuts'}
              period={period}
            />
          </Col>
          <Col xs={24} md={24} lg={12}>
            <BarChart
              title={'Browsers'}
              data={statistics?.browsers}
              xField={'browser'}
              yField={'visits'}
              xTitle={'Browser'}
              yTitle={'Usage %'}
              period={period}
            />
          </Col>
          <Col xs={24} md={24} lg={12}>
            <BarChart
              title={'Operating Systems'}
              data={statistics?.osNames}
              xField={'osName'}
              yField={'visits'}
              xTitle={'OS Name'}
              yTitle={'Usage %'}
              period={period}
            />
          </Col>
        </Row>
      </div>
    </div>
  );

  return isLoading ? <Spin>{content}</Spin> : content;
};

export default Statistics;
